// packages/studio/web/src/editor/route.tsx
//
// Editor route: edits flow through a *changeset* (Studio's "propose, don't
// commit" model). You always edit an active changeset:
//   • draft  — no git; edits autosave as proposal-scoped drafts. Toolbar
//              action is "Propose…" (fork branch + squash drafts → open).
//   • proposed — edits autosave as drafts on top of the feature branch head;
//              top-bar action is "Update" (commit them) + a "Review" button that
//              steps aside while the review tab (which hoists its own chrome up
//              there) is the active tab.
//
// Review (the diff + comments + Accept/Archive/Update-from-trunk) is NOT part of
// this route: it's a plugin custom tab contributed by historyPlugin (the F4
// thesis test) — opened via the Proposals nav section, the "Review" button, or a
// `?view=diff` deep link. This route only mounts the workbench + plugins and
// keeps the draft-editing chrome (switcher / Propose / Update / Revert).
//
// The active changeset is resolved from its per-repo number — the URL key
// (`/:ws/:repo/:num/<path>`). The base ref (trunk for a draft, feature
// branch for a proposed one) is derived from the changeset, not the URL. The
// bare repo URL (`/:ws/:repo`) resolves the unnamed draft and redirects
// to its number (see RepoHomeRedirect).

import { useEffect, useMemo, useRef, useState } from "react";
import {
  WorkbenchPluginHost,
  WorkbenchHostProvider,
  RegistryBridge,
  type HostCapabilities,
} from "@archlang/app-shell/board";
import type { TabSpec } from "@archlang/app-shell/workbench";
import { otlpPlugin } from "./plugins/otlp/otlpPlugin.js";
import {
  historyPlugin,
  openReviewTab,
  useHistoryStore,
  setProposeOpen,
} from "./plugins/history/index.js";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { seedVfs } from "./seedVfs.js";
import { createRequestedFile } from "./createRequestedFile.js";
import { isMarkdownPath } from "@archlang/viewer-core";
import {
  getChangesetByNumber,
  fetchDrafts,
  deleteDraft,
  renameChangeset,
  updateProposal,
  type Changeset,
} from "../proposals/proposalsApi.js";
import { changeset as changesetPath, repoHome } from "../lib/paths.js";
import {
  cacheChangeset,
  invalidateChangesetLists,
} from "../proposals/changesetCache.js";
import { ChangeSetSwitcher } from "../proposals/ChangeSetSwitcher.js";
import { ProposeModal } from "../proposals/ProposeModal.js";
import { ChangeDocButton } from "../proposals/ChangeDocButton.js";
import { useMe } from "../api/hooks/useMe.js";
import { ApiError, API_BASE } from "../api/client.js";
import { useWorkspaceFiles, useDirtyPaths, useTabs, useActiveTabId } from "@archlang/app-shell/workbench";
import { useStudioWorkspace } from "./useStudioWorkspace.js";
import { StudioComposition } from "./StudioComposition.js";
import LspWorker from "./lsp-worker.ts?worker";
import { Tooltip } from "@archlang/ui";
import { Button } from "../ui/Button.js";
import { Spinner } from "../ui/Spinner.js";
import { useToast } from "../ui/Toast.js";
import { HeaderSlotPortal } from "../app/headerSlot.js";
import { useTranslation } from "../i18n";

const EMPTY_MAP: ReadonlyMap<string, string> = new Map();

// One worker factory per mount — the shared Editor owns the LSP bridge lifecycle.
const createLspWorker = () => new LspWorker();

export function EditorRoute() {
  const { slug, repo, num, "*": splat } = useParams() as Record<string, string>;
  const path = splat ?? "";
  const csNumber = Number(num);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // In-app navigations (open file, delete-and-fallback, home redirect) must
  // carry the current query through — otherwise an in-flight query param is
  // dropped on the first file open. Kept as a string (no leading "?";
  // react-router adds it).
  const navSearch = searchParams.toString();
  const { t } = useTranslation("editor");
  const toast = useToast();
  const queryClient = useQueryClient();

  // 0. Resolve the active changeset from its per-repo number (the URL key).
  //    Drafts/autosave are scoped to its (ULID) id; the base ref is derived,
  //    so neither needs to live in the URL.
  const csQuery = useQuery({
    queryKey: ["changeset", slug, repo, csNumber],
    queryFn: () => getChangesetByNumber(slug, repo, csNumber),
    enabled: Number.isInteger(csNumber) && csNumber > 0,
  });
  const changeset = csQuery.data ?? null;
  const csId = changeset?.id ?? null;
  const baseRef = changeset ? changeset.featureBranch ?? changeset.trunkBranch : null;

  // Editing is author-only. Others can open a change to review (read-only diff)
  // but not edit it. Optimistic while `me` is still loading.
  const meId = useMe().data?.id ?? null;
  const isAuthor = meId == null || !changeset || changeset.authorUserId === meId;

  // 1. Branch tip for empty-repo detection (a brand-new trunk has no commits).
  const tipQuery = useQuery({
    queryKey: ["ref-tip", slug, repo, baseRef],
    queryFn: async (): Promise<string | null> => {
      const r = await fetch(`${API_BASE}/api/ws/${encodeURIComponent(slug)}/r/${encodeURIComponent(repo)}/refs`, { credentials: "include" });
      if (!r.ok) throw new Error(`refs fetch failed: ${r.status}`);
      const body = (await r.json()) as { refs?: { name: string; oid: string }[] };
      const found = (body.refs ?? []).find((x) => x.name === `refs/heads/${baseRef}`);
      return found?.oid ?? null;
    },
    enabled: !!changeset,
  });

  // 2. Seed the editor: committed content from `baseRef` (empty for a brand-
  //    new trunk with no commits — propose makes the initial commit), overlaid
  //    with the changeset's drafts → the working map.
  const vfsQuery = useQuery({
    queryKey: ["vfs-seed", slug, repo, baseRef, csId],
    queryFn: async () => {
      const committed = tipQuery.data ? await seedVfs({ slug, repo, ref: baseRef!, apiBase: API_BASE }) : new Map<string, string>();
      const drafts = csId ? await fetchDrafts(slug, repo, csId) : [];
      const working = new Map(committed);
      const tombstones = new Set<string>();
      for (const d of drafts) {
        if (d.deleted) {
          working.delete(d.path);
          tombstones.add(d.path);
        } else {
          working.set(d.path, d.content);
        }
      }
      return {
        committed,
        working,
        draftPaths: new Set(drafts.filter((d) => !d.deleted).map((d) => d.path)),
        tombstones,
      };
    },
    enabled: !!baseRef && tipQuery.isSuccess && csId != null,
  });

  // Adopt the shared WorkspaceStore: base = committed trunk, working =
  // committed + drafts. The store owns file content + dirty; the hook persists
  // mutations to the drafts API (base pinned → dirty == modified-vs-trunk).
  const { store, ready: wsReady, drafts } = useStudioWorkspace({
    vfsData: vfsQuery.data,
    csId,
    slug,
    repo,
    activePath: path,
    // Autosave is otherwise fire-and-forget; surface failures so an edit is never
    // silently lost. A 402 means the workspace storage cap was hit.
    onSaveError: (err, p) => {
      const quota = err instanceof ApiError && err.status === 402;
      toast.error(
        quota
          ? t("save.storageFull", { path: p })
          : err instanceof Error
            ? t("save.failedWithReason", { path: p, reason: err.message })
            : t("save.failed", { path: p }),
      );
    },
  });
  const files = useWorkspaceFiles(store);
  const dirty = useDirtyPaths(store);
  // The review tab hoists its own chrome (view modes + accept/archive/update-from-
  // trunk) into the top bar's tab slot, so the button that OPENS it would sit
  // right next to them pointing at where you already are.
  const tabs = useTabs(store);
  const activeTabId = useActiveTabId(store);
  const reviewTabActive = tabs.some(
    (tb) => tb.id === activeTabId && tb.kind === "custom" && tb.state.panelKind === "history:review",
  );
  const committed = vfsQuery.data?.committed ?? EMPTY_MAP;

  // Workbench plugins Studio opts into. The OTLP telemetry plugin owns the
  // live-traffic store + its SSE feed and contributes the request-flow overlay
  // to the board — the board itself carries no telemetry code. Memoized per repo
  // so the plugin's provider isn't remounted every render.
  const workbenchPlugins = useMemo(
    () => [otlpPlugin({ slug, repo }), historyPlugin()],
    [slug, repo],
  );

  // The host capabilities the actionable plugin seams (menus, context menus,
  // settings pages) resolve `when(ctx)`/`run(ctx)` against. `activeRepo` is the
  // repo the settings-page + menu gates key on. Mounted below the plugin host so
  // the RegistryBridge (also below) mirrors the built registry + this ctx to the
  // out-of-host menu surfaces (brand menu, settings modal).
  const hostCaps = useMemo<HostCapabilities>(
    () => ({
      activeRepo: () => ({ slug, repo }),
      // `ctx.openTab` → the workspace store's tab list, so a plugin (the history
      // nav section) can open its custom review tab.
      openTab: (spec) => void store.openTab(spec as TabSpec),
      toast: (m) => (m.tone === "error" ? toast.error(m.message) : toast.success(m.message)),
    }),
    [slug, repo, toast, store],
  );

  // LSP is owned by the shared <Editor> (it registers all 14 Monaco language
  // providers and seeds the worker from the store's files) — no manual bridge
  // wiring here. Keep the open file in sync between the URL and the store so the
  // Editor renders the right file (the URL is Studio's active-file truth).
  useEffect(() => {
    if (!path) return;
    if (isMarkdownPath(path)) {
      store.openTab({ kind: "doc", path });
    } else {
      store.setActiveFile(path);
    }
  }, [path, store]);

  // The repo-browser "New file" hand-off. `?new=<path>` names a file that does
  // not exist yet, so merely OPENING it mints a phantom tab: `reconcileFileTabs`
  // drops file tabs the VFS doesn't hold, and the hydrate that always follows
  // the seed reaped it — the named file never existed. Create it for real (a
  // manifest seeded, since an empty one is a parse error), then drop the param
  // so this can't fire twice or resurrect the file after a delete/revert.
  useEffect(() => {
    const wanted = searchParams.get("new");
    if (!wanted || !wsReady || !changeset) return;
    const outcome = createRequestedFile(store, files, wanted);
    if (outcome === "refused") {
      // The dialog validates path SHAPE but has no directory listing, so it
      // cannot know the name is taken by a directory (or sits under a file).
      // The workspace refuses silently — say so here, and stay put rather than
      // navigating to a file that does not exist with the param already gone.
      toast.error(t("toast.newFileRefused", { path: wanted }));
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete("new");
          return next;
        },
        { replace: true },
      );
      return;
    }
    navigate(
      { pathname: changesetPath(slug, repo, changeset.number, wanted), search: "" },
      { replace: true },
    );
  }, [searchParams, setSearchParams, wsReady, files, store, navigate, slug, repo, changeset, toast, t]);

  // No "land on the first file" effect: an empty path IS the destination.
  // Auto-navigating to the alphabetically-first file made every project open
  // mint a second, pinned tab beside Overview — the app guessing which file you
  // wanted, and guessing by sort order. Overview is the project home; the user
  // picks what to open from there.

  // Review is a plugin custom tab now (historyPlugin `history:review`), opened
  // from the Proposals nav section. Non-authors (no edit rights) and `?view=diff`
  // deep links land straight on it. Guarded so it opens once per changeset.
  const openedReviewFor = useRef<string | null>(null);
  useEffect(() => {
    // Leaving a changeset resets its "already opened" mark — the review tab's
    // lifetime is "until switch-away" (the switch reseeds tabs cold), so a
    // deep-link BACK to a proposed changeset must reopen its review.
    if (openedReviewFor.current && openedReviewFor.current !== csId) {
      openedReviewFor.current = null;
    }
    // Wait for the workspace to hydrate: the first (cold) vfs seed reseeds the
    // tab list, so opening the review tab before it lands would get wiped.
    if (!wsReady || !changeset || changeset.state !== "proposed" || !csId) return;
    const wantReview = !isAuthor || searchParams.get("view") === "diff";
    if (!wantReview || openedReviewFor.current === csId) return;
    openedReviewFor.current = csId;
    openReviewTab((spec) => store.openTab(spec as TabSpec), slug, repo, changeset);
  }, [wsReady, changeset, isAuthor, csId, searchParams, slug, repo, store]);

  // ---- changeset lifecycle (propose / update / rename) --------------------
  // The change name doubles as the proposal title — proposing needs no extra
  // prompt. Controlled here so Propose reads the latest value synchronously.
  const [changeName, setChangeName] = useState("");
  useEffect(() => {
    setChangeName(changeset?.title ?? "");
  }, [changeset?.id, changeset?.title]);

  const updateMutation = useMutation({
    mutationFn: async () => {
      // Commit what the user SEES: an edit made within the autosave debounce
      // window must land in the draft rows before the server squashes them —
      // otherwise Update silently drops the last keystrokes.
      await drafts.flushAll();
      return updateProposal(slug, repo, csId!);
    },
    onSuccess: (cs) => {
      toast.success(t("toast.proposalUpdated"));
      // The vfs-seed refetch (committed now includes the update, drafts cleared)
      // re-hydrates the store → dirty clears on its own.
      cacheChangeset(queryClient, slug, repo, cs);
      queryClient.invalidateQueries({ queryKey: ["vfs-seed", slug, repo, baseRef, cs.id] });
      invalidateChangesetLists(queryClient, slug, repo);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const renameMutation = useMutation({
    mutationFn: (title: string) => renameChangeset(slug, repo, csId!, title),
    onSuccess: (cs) => {
      cacheChangeset(queryClient, slug, repo, cs);
      invalidateChangesetLists(queryClient, slug, repo);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  // Propose collects the title + description + target in a modal (authored
  // change content welded into the commit). The modal open flag lives in the
  // historyStore so the "Propose changes" plugin command opens it too; route.tsx
  // still owns the modal itself (it needs the workspace store's dirty set, which
  // a plugin provider can't read).
  const history = useHistoryStore();
  const proposeOpen = history.proposeOpen;
  // `proposeOpen` is a module singleton (shared with the plugin's command), so
  // it outlives a mount — an abandoned Propose (opened, then navigated away)
  // would re-pop on the next changeset. Clear it whenever the changeset changes
  // (fires on mount too), matching the pre-plugin per-mount `useState` reset.
  useEffect(() => {
    setProposeOpen(false);
  }, [csId]);
  // "New proposal" plugin command → navigate to a fresh unnamed draft.
  const newProposalNonce = history.newProposalNonce;
  const ackedNewProposal = useRef(newProposalNonce);
  useEffect(() => {
    if (ackedNewProposal.current === newProposalNonce) return;
    ackedNewProposal.current = newProposalNonce;
    navigate(repoHome(slug, repo));
  }, [newProposalNonce, navigate, slug, repo]);
  function handleProposed(cs: Changeset) {
    // The number is stable across propose, so the URL doesn't change — only the
    // changeset's state + base ref do. Refresh the detail cache so the editor
    // re-derives baseRef (now the feature branch) and reseeds the VFS — which
    // re-hydrates the store (committed = branch, drafts cleared → dirty clears).
    cacheChangeset(queryClient, slug, repo, cs);
    invalidateChangesetLists(queryClient, slug, repo);
  }

  // Accept / Archive / Update-from-trunk are the review tab's own chrome now
  // (ReviewTabRenderer, historyPlugin) — they moved off the route with the diff
  // surface. The route keeps only draft-editing lifecycle (propose / update /
  // rename / revert) below.

  // Discard every draft in the changeset and return the working set to trunk
  // (Main). Drops draft rows + tombstones server-side, then reseeds from
  // committed content.
  const revertMutation = useMutation({
    mutationFn: async () => {
      const id = csId!;
      // Cancel pending autosaves FIRST — the deletes + refetch below are async
      // round-trips, and a debounce timer firing in that window would write the
      // just-reverted content straight back as a draft.
      drafts.cancelAll();
      // Drop every draft / tombstone (dirtyPaths covers modified + new +
      // deleted-committed). The vfs-seed refetch then re-hydrates the store back
      // to committed.
      await Promise.all([...dirty].map((p) => deleteDraft(slug, repo, id, p)));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["vfs-seed", slug, repo, baseRef, csId] });
      // If the open file was a newly-added (now-gone) file, fall back to
      // Overview — its model would otherwise sit on a dead path. Guarded on a
      // non-empty `path`: reverting FROM Overview has no dead path to rescue,
      // and the old unguarded form bounced you onto an arbitrary file (`""` is
      // never a `committed` key), reintroducing the very tab this route no
      // longer opens.
      if (path && !committed.has(path)) {
        navigate(
          { pathname: changesetPath(slug, repo, csNumber), search: navSearch },
          { replace: true },
        );
      }
      toast.success(t("toast.reverted"));
    },
    onError: (err: Error) => toast.error(err.message),
  });

  // ---- render gates -------------------------------------------------------
  // A non-numeric / non-positive changeset segment can't resolve — the changeset
  // query is disabled, so without this it would spin forever (bugloop L2 #3).
  if (!Number.isInteger(csNumber) || csNumber <= 0) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 p-8 text-center">
        <p className="text-sm font-medium text-red-300">{t("notFound.title")}</p>
        <p className="max-w-md font-mono text-xs text-slate-500">{t("notFound.detail", { num })}</p>
      </div>
    );
  }
  if (csQuery.isError || tipQuery.isError || vfsQuery.isError) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 p-8 text-center">
        <p className="text-sm font-medium text-red-300">{t("loadFailed.title")}</p>
        <p className="max-w-md font-mono text-xs text-slate-500">
          {String((csQuery.error ?? tipQuery.error ?? vfsQuery.error) as Error)}
        </p>
      </div>
    );
  }

  if (!changeset || !vfsQuery.data || !wsReady) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 p-8 text-slate-400">
        <Spinner />
        <p className="text-sm">{t("loading")}</p>
      </div>
    );
  }

  const hasDrafts = dirty.size > 0;
  const isDraft = changeset.state === "draft";

  // The changeset proposal chrome (ChangeSetSwitcher + name field +
  // Revert/Propose/Review/Update/Accept/Archive) lives in a HeaderSlotPortal —
  // it portals into the app top bar, OUTSIDE the editor body, so it survives a
  // surface swap (e.g. editor ↔ diff).
  const proposalChrome = (
    <HeaderSlotPortal>
      <span className="h-5 w-px bg-slate-800" aria-hidden="true" />
      <ChangeSetSwitcher slug={slug} repo={repo} active={changeset} hasChanges={hasDrafts} />
      {isAuthor && csId && baseRef && (isDraft || changeset.state === "proposed") ? (
        <ChangeDocButton
          slug={slug}
          repo={repo}
          changeset={changeset}
          store={store}
          touchedPaths={[...dirty]}
          baseRef={baseRef}
          title={changeName}
        />
      ) : null}
      {/* Name/status live in the switcher dropdown. Only an unnamed draft with
          changes gets an inline field — to name it before proposing. */}
      {isDraft && hasDrafts && !changeset.title.trim() ? (
        <input
          aria-label={t("chrome.changeNameAria")}
          value={changeName}
          onChange={(e) => setChangeName(e.target.value)}
          placeholder={t("chrome.changeNamePlaceholder")}
          onBlur={(e) => {
            const v = e.target.value.trim();
            if (v && v !== changeset.title) renameMutation.mutate(v);
          }}
          className="min-w-0 max-w-[20rem] flex-1 rounded-md border border-transparent bg-transparent px-2 py-1 text-sm text-slate-200 hover:border-slate-700 focus:border-slate-600 focus:outline-none"
        />
      ) : null}
      <span className="flex-1" />
      {isDraft ? (
        hasDrafts ? (
          <>
            {!changeset.title.trim() ? (
              <Tooltip content={t("chrome.revertTooltip")}>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={revertMutation.isPending}
                  onClick={() => revertMutation.mutate()}
                >
                  {revertMutation.isPending ? t("chrome.reverting") : t("chrome.revertToMain")}
                </Button>
              </Tooltip>
            ) : null}
            <Button
              variant="primary"
              size="sm"
              onClick={() => setProposeOpen(true)}
            >
              {t("chrome.propose")}
            </Button>
          </>
        ) : null
      ) : changeset.state === "proposed" && isAuthor ? (
        <>
          {/* Open the review as a plugin tab (accept/archive/update-from-trunk
              live in that tab now); commit more drafts onto the branch here. */}
          {reviewTabActive ? null : (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                if (!csId) return;
                openReviewTab((spec) => store.openTab(spec as TabSpec), slug, repo, changeset);
              }}
            >
              {t("chrome.review")}
            </Button>
          )}
          <Button
            variant="primary"
            size="sm"
            disabled={!hasDrafts || updateMutation.isPending}
            onClick={() => updateMutation.mutate()}
          >
            {updateMutation.isPending ? t("chrome.updating") : t("chrome.update")}
          </Button>
        </>
      ) : null}
    </HeaderSlotPortal>
  );

  return (
    <>
      {proposalChrome}
      {csId && baseRef ? (
        // Keyed by changeset: the modal holds description/target as component
        // state, and on a cached switch it never unmounts — without the key,
        // changeset A's typed description + target pick prefill changeset B's
        // proposal form (and A's stale description lands in B's commit body).
        <ProposeModal
          key={csId}
          open={proposeOpen}
          onClose={() => setProposeOpen(false)}
          slug={slug}
          repo={repo}
          changesetId={csId}
          initialTitle={changeName}
          baseRef={baseRef}
          touchedPaths={[...dirty]}
          onBeforePropose={() => drafts.flushAll()}
          onSuccess={handleProposed}
        />
      ) : null}
      {/* Key on the changeset number so switching changesets remounts the editor
          (fresh LSP worker + viewport). The worker only re-seeds on a file-path-set
          change, so without this a same-file-set swap keeps stale content for
          unopened files → wrong cross-file diagnostics. The number is stable across
          propose/update, so this never remounts mid-lifecycle. */}
      <WorkbenchPluginHost plugins={workbenchPlugins}>
      <WorkbenchHostProvider capabilities={hostCaps}>
      <RegistryBridge />
      <StudioComposition
        key={csNumber}
        persistNamespace={`studio/${slug}/${repo}`}
        store={store}
        ready={wsReady}
        changesetLabel={changeset?.title?.trim() ? changeset.title : `#${csNumber}`}
        createLspWorker={createLspWorker}
      />
      </WorkbenchHostProvider>
      </WorkbenchPluginHost>
    </>
  );
}
